/**
 * Snapshot-based undo/redo history module.
 *
 * Manages two stacks: undoStack and redoStack.
 * The caller is responsible for deep-copying snapshot data before calling pushSnapshot.
 *
 * Snapshot shape (opaque to this module):
 *   { polygons: Array, selectedId: string|null }
 *
 * Buffer: max 10 entries (FIFO — oldest dropped when full).
 */

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

const MAX_HISTORY = 10;

/** @type {Array<any>} */
let undoStack = [];

/** @type {Array<any>} */
let redoStack = [];

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * Push a snapshot onto the undo stack. Clears redo stack.
 * Drops oldest entry when buffer exceeds MAX_HISTORY (FIFO).
 *
 * @param {any} snapshot - Opaque snapshot object (caller handles deep copy)
 */
export function pushSnapshot(snapshot) {
  undoStack.push(snapshot);
  if (undoStack.length > MAX_HISTORY) {
    undoStack.shift(); // drop oldest (FIFO)
  }
  redoStack = []; // new action clears redo branch
}

/**
 * Pop the most recent snapshot from undoStack.
 * Pushes currentSnapshot onto redoStack for future redo.
 * Returns the snapshot to restore, or null if stack is empty.
 *
 * @param {any} currentSnapshot - The caller's current state snapshot
 * @returns {any|null}
 */
export function undo(currentSnapshot) {
  if (undoStack.length === 0) return null;
  redoStack.push(currentSnapshot);
  return undoStack.pop();
}

/**
 * Pop the most recent entry from redoStack.
 * Pushes currentSnapshot back onto undoStack.
 * Returns the snapshot to restore, or null if stack is empty.
 *
 * @param {any} currentSnapshot - The caller's current state snapshot
 * @returns {any|null}
 */
export function redo(currentSnapshot) {
  if (redoStack.length === 0) return null;
  undoStack.push(currentSnapshot);
  return redoStack.pop();
}

/**
 * Clear both stacks. Call on file open / new document.
 */
export function clearHistory() {
  undoStack = [];
  redoStack = [];
}

/**
 * Return true if there is something to undo.
 *
 * @returns {boolean}
 */
export function canUndo() {
  return undoStack.length > 0;
}

/**
 * Return true if there is something to redo.
 *
 * @returns {boolean}
 */
export function canRedo() {
  return redoStack.length > 0;
}

/**
 * Reset module state for testing purposes.
 * Equivalent to clearHistory() — use in beforeEach of test files.
 */
export function resetForTesting() {
  clearHistory();
}
